import html2canvas from 'html2canvas';
import { formatDistance, getScoreRating } from './distanceUtils';

/**
 * Generate a shareable image of the game result
 * @param {Object} options - Screenshot options
 * @param {HTMLElement} options.mapContainer - Map container element
 * @param {number} options.score - Game score
 * @param {number} options.distance - Distance in meters
 * @param {Object} options.stats - Comparison stats (optional)
 * @returns {Promise<Blob>} - Image blob
 */
export async function generateShareImage({ mapContainer, score, distance, stats }) {
  try {
    // Create a temporary container for the shareable card
    const shareCard = document.createElement('div');
    shareCard.style.position = 'fixed';
    shareCard.style.left = '-9999px';
    shareCard.style.width = '800px';
    shareCard.style.padding = '40px';
    shareCard.style.background = 'linear-gradient(135deg, #0A0A0A 0%, #1A1A1A 100%)';
    shareCard.style.borderRadius = '16px';
    shareCard.style.fontFamily = 'system-ui, -apple-system, sans-serif';

    // Build the content
    let statsHtml = '';
    if (stats && stats.percentile !== null) {
      statsHtml = `
        <div style="margin-top: 24px; padding: 20px; background: rgba(139, 157, 131, 0.1); border-radius: 12px;">
          <p style="font-size: 14px; color: #8B9D83; margin: 0 0 8px 0; text-align: center;">Better than ${stats.percentile}% of players today</p>
          ${stats.totalPlayers ? `<p style="font-size: 12px; color: #E8DCC4; opacity: 0.7; margin: 0; text-align: center;">${stats.totalPlayers} total players</p>` : ''}
        </div>
      `;
    }

    shareCard.innerHTML = `
      <div style="text-align: center;">
        <h1 style="font-size: 32px; color: #E8DCC4; margin: 0 0 24px 0;">Campus Guesser</h1>

        <div style="margin: 24px 0; padding: 24px; background: rgba(139, 157, 131, 0.1); border-radius: 12px;">
          <p style="font-size: 14px; color: #E8DCC4; opacity: 0.7; margin: 0 0 8px 0; text-transform: uppercase; letter-spacing: 0.1em;">Score</p>
          <p style="font-size: 56px; font-weight: 700; color: #8B9D83; margin: 8px 0; font-family: monospace;">${score}</p>
          <p style="font-size: 16px; color: #E8DCC4; margin: 4px 0 0 0;">${getScoreRating(score)}</p>
        </div>

        <div style="margin: 24px 0; padding: 20px; background: rgba(139, 157, 131, 0.1); border-radius: 12px;">
          <p style="font-size: 14px; color: #E8DCC4; opacity: 0.7; margin: 0 0 8px 0; text-transform: uppercase; letter-spacing: 0.1em;">Distance</p>
          <p style="font-size: 20px; font-weight: 600; color: #E8DCC4; margin: 0;">${formatDistance(distance)}</p>
        </div>

        ${statsHtml}

        <div style="margin-top: 32px; padding-top: 24px; border-top: 1px solid #3A4238;">
          <p style="font-size: 14px; color: #E8DCC4; opacity: 0.7; margin: 0;">Hugames - Campus Guesser</p>
        </div>
      </div>
    `;

    document.body.appendChild(shareCard);

    // Generate canvas
    const canvas = await html2canvas(shareCard, {
      backgroundColor: '#0A0A0A',
      scale: 2,
      logging: false
    });

    // Remove temporary element
    document.body.removeChild(shareCard);

    // Convert to blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/png');
    });
  } catch (error) {
    console.error('Error generating share image:', error);
    throw error;
  }
}

/**
 * Generate text-based share message
 * @param {Object} options - Message options
 * @param {number} options.score - Game score
 * @param {number} options.distance - Distance in meters
 * @param {Object} options.stats - Comparison stats (optional)
 * @returns {string} - Formatted share text
 */
export function generateShareText({ score, distance, stats }) {
  let text = `🎯 Campus Guesser\n\n`;
  text += `Score: ${score} - ${getScoreRating(score)}\n`;
  text += `Distance: ${formatDistance(distance)}\n`;

  if (stats && stats.percentile !== null) {
    text += `\nBetter than ${stats.percentile}% of players!\n`;
  }

  text += `\nPlay at https://tribe.college!`;

  return text;
}

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} - Success status
 */
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      return success;
    }
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return false;
  }
}

/**
 * Copy image to clipboard
 * @param {Blob} imageBlob - Image blob
 * @returns {Promise<boolean>} - Success status
 */
export async function copyImageToClipboard(imageBlob) {
  try {
    if (navigator.clipboard && navigator.clipboard.write) {
      const item = new ClipboardItem({ 'image/png': imageBlob });
      await navigator.clipboard.write([item]);
      return true;
    } else {
      console.warn('Clipboard API not supported for images');
      return false;
    }
  } catch (error) {
    console.error('Error copying image to clipboard:', error);
    return false;
  }
}

/**
 * Download image file
 * @param {Blob} imageBlob - Image blob
 * @param {string} filename - Filename for download
 */
export function downloadImage(imageBlob, filename = 'campus-guesser-result.png') {
  const url = URL.createObjectURL(imageBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Share using Web Share API (mobile)
 * @param {Object} options - Share options
 * @param {string} options.text - Share text
 * @param {Blob} options.image - Image blob (optional)
 * @returns {Promise<boolean>} - Success status
 */
export async function shareViaWebAPI({ text, image }) {
  try {
    if (!navigator.share) {
      console.warn('Web Share API not supported');
      return false;
    }

    const shareData = {
      text: text
    };

    // Add image if provided and supported
    if (image && navigator.canShare) {
      const file = new File([image], 'campus-guesser-result.png', { type: 'image/png' });
      if (navigator.canShare({ files: [file] })) {
        shareData.files = [file];
      }
    }

    await navigator.share(shareData);
    return true;
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('Error sharing:', error);
    }
    return false;
  }
}
